import React from "react"

// Models
import {Beat} from "./model"

/**
 * How a save ended:
 *   saved    the server has these beats
 *   retry    did not get through — network, 5xx, 408, 429 — try again later
 *   refused  the server said no (any other 4xx): sending the same beats again
 *            will not change its mind, so no retry until the beats change
 */
export type SaveOutcome = "saved" | "retry" | "refused"

/**
 * Saves the beats. The browser copy is the caller's to write first,
 * synchronously.
 */
export type SaveBeats = (beats: Beat[]) => Promise<SaveOutcome>

export type AutosaveState = {
    /** Something on screen the server does not have yet, or a save in flight. */
    unsaved: boolean
    /** The last attempt failed and is being retried with backoff. */
    failing: boolean
    /** The server refused the last save; nothing is retried until the beats change. */
    refused: boolean
    /** Send what is on screen now, without waiting for the quiet period. */
    flush(): void
}

const RETRY_FIRST_MS = 2000;
const RETRY_MAX_MS = 30000;

/**
 * Save the beats a moment after they last changed, whenever they differ from
 * what the server last confirmed — and never lose them on the way out.
 *
 * "Differ from what the server confirmed", not "some beat was reviewed": an
 * undo back to the untouched marks is a change like any other, and the server
 * must end up with what the screen shows. Compared by identity — every edit
 * makes a new array, and an undo hands back the earlier one.
 *
 * A save counts only once the server confirms it. Until then the work stays
 * "unsaved": a failure is retried with backoff, and approve waits.
 *
 * Saves go one at a time. Two PUTs in flight could land out of order and
 * leave the server with the older one.
 *
 * Nothing pending is dropped: turning annotation off, moving to another
 * record and leaving the page all send it at once — with the save that
 * belongs to the record the beats came from, never the next one's.
 *
 * @param beats the beats on screen
 * @param baseline what the server gave — saving it back would change nothing
 * @param enabled false while annotation is off, or the beats are not this record's yet
 * @param save does the saving
 * @param key the record (and rate) the beats belong to
 * @param delayMs quiet time before saving, so a burst of arrow presses is one save
 */
export default function useAutosave(
    beats: Beat[],
    baseline: Beat[],
    enabled: boolean,
    save: SaveBeats,
    key: string | null,
    delayMs: number = 1000,
): AutosaveState {
    const lastSaved = React.useRef<Beat[]>(baseline);
    const inFlight = React.useRef<Beat[] | null>(null);
    /* a flush asked for while a save was in flight: send again when it lands */
    const again = React.useRef(false);
    const [failures, setFailures] = React.useState(0);
    /* the beats the server refused — not sent again as they are */
    const [refusedBeats, setRefusedBeats] = React.useState<Beat[] | null>(null);
    /* bumped when a save lands, so the effect below looks again */
    const [tick, setTick] = React.useState(0);
    const saveRef = React.useRef(save);

    saveRef.current = save;

    /* what the last COMMITTED render had — read by the flushes on the way out,
       which must use the record the beats belong to */
    const committed = React.useRef({beats: beats, save: save, key: key, enabled: enabled, lastSaved: baseline});
    const keyRef = React.useRef(key);

    /* new labels from the server are, by definition, what the server has */
    React.useEffect(() => {
        lastSaved.current = baseline;
        setFailures(0)
    }, [baseline]);

    /* a new edit gets its own first try, not the tail of the last one's backoff */
    React.useEffect(() => {
        setFailures(0)
    }, [beats]);

    const run = React.useCallback((toSave: Beat[], saveIt: SaveBeats, forKey: string | null) => {
        if (inFlight.current) {
            again.current = true;

            return
        }

        inFlight.current = toSave;
        setTick(n => n + 1);

        saveIt(toSave).then(outcome => outcome, (): SaveOutcome => "retry").then(outcome => {
            inFlight.current = null;

            /* a record left meanwhile: its result is not this one's */
            if (forKey === keyRef.current) {
                if (outcome === "saved") {
                    lastSaved.current = toSave
                }

                setRefusedBeats(outcome === "refused" ? toSave : null);
                setFailures(f => (outcome === "retry" ? f + 1 : 0))
            }

            /* a flush that came in while this was in flight */
            const latest = committed.current;

            if (again.current && latest.key === forKey && latest.beats !== lastSaved.current) {
                again.current = false;
                run(latest.beats, latest.save, latest.key)
            } else {
                again.current = false
            }

            setTick(n => n + 1)
        })
    }, []);

    /* the quiet-period save, and the retries — re-armed only by what matters,
       so an unrelated re-render of the page cannot keep postponing it */
    React.useEffect(() => {
        /* refused as they stand: the next edit will be tried, not these again */
        if (!enabled || !beats.length || beats === lastSaved.current || inFlight.current || beats === refusedBeats) {
            return
        }

        const wait = failures === 0
            ? delayMs
            : Math.min(RETRY_MAX_MS, RETRY_FIRST_MS * Math.pow(2, failures - 1));
        const timer = window.setTimeout(() => run(beats, saveRef.current, key), wait);

        return () => window.clearTimeout(timer)
    }, [beats, enabled, failures, tick, delayMs, key, run, refusedBeats]);

    /* annotation turned off, or the beats stopped being this record's: send
       what is pending now, with the save of the record they came from */
    const wasEnabled = React.useRef(enabled);

    React.useEffect(() => {
        const before = committed.current;

        /* compared with what THAT record last saved — in a record switch the
           baseline above has already moved on to the next one */
        if (wasEnabled.current && !enabled && before.beats.length && before.beats !== before.lastSaved) {
            run(before.beats, before.save, before.key)
        }

        wasEnabled.current = enabled
    }, [enabled, run]);

    /* another record: flush the one being left */
    React.useEffect(() => {
        keyRef.current = key;

        return () => {
            const before = committed.current;

            if (before.enabled && before.beats.length && before.beats !== before.lastSaved) {
                run(before.beats, before.save, before.key)
            }
        }
    }, [key, run]);

    /* after the effects above have read the previous commit: remember this one */
    React.useEffect(() => {
        committed.current = {beats: beats, save: save, key: key, enabled: enabled, lastSaved: lastSaved.current}
    });

    /* leaving the page: write the browser copy and try the server; and ask
       before closing on unsaved work */
    React.useEffect(() => {
        function pending(): boolean {
            const now = committed.current;

            return now.enabled && now.beats.length > 0 && (now.beats !== lastSaved.current || inFlight.current !== null)
        }

        function onHide() {
            const now = committed.current;

            if (pending() && now.beats !== lastSaved.current) {
                run(now.beats, now.save, now.key)
            }
        }

        function onBeforeUnload(event: BeforeUnloadEvent) {
            if (pending()) {
                onHide();
                event.preventDefault();
                event.returnValue = ""
            }
        }

        window.addEventListener("pagehide", onHide);
        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            window.removeEventListener("pagehide", onHide);
            window.removeEventListener("beforeunload", onBeforeUnload)
        }
    }, [run]);

    const flush = React.useCallback(() => {
        const now = committed.current;

        if (now.beats.length && now.beats !== lastSaved.current) {
            run(now.beats, now.save, now.key)
        }
    }, [run]);

    return {
        unsaved: enabled && beats.length > 0 && (beats !== lastSaved.current || inFlight.current !== null),
        failing: failures > 0,
        refused: refusedBeats !== null && refusedBeats === beats,
        flush: flush,
    }
}
